// Faqs/index.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  FaqSectionsWrapper,
  FaqSectionWrapper,
  FaqSectionTitle,
  FaqQuestionsWrapper,
  FaqQuestionWrapper,
  FaqQuestionTitle,
  FaqQuestionAnswer,
} from './styled'

import {
  SectionHeader,
  SectionHeaderContainerProps,
} from '../../components/SectionHeader'

// =======================
// Types
// =======================
export interface QuestionProps {
  question: string
  answer: React.ReactNode
  sectionID?: string
}

export interface SectionProps {
  sectionID: string
  sectionTitle: string
  questions: Array<QuestionProps>
}

export interface FaqsProps {
  sectionHeaderProps?: SectionHeaderContainerProps
  faqSections?: Array<SectionProps>
  onAccordionToggle?: (payload: {
    key: string
    state: 'open' | 'closed'
    level: 'section' | 'question'
  }) => void
}

// =======================
// Utilities
// =======================
const isBrowser = typeof window !== 'undefined'

const toKey = (str: string) =>
  str
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Height helpers
const initCollapsed = (el: HTMLElement | null, active: boolean) => {
  if (!el) return
  el.style.maxHeight = active ? 'none' : '0px'
}

const animateHeight = (el: HTMLElement | null, expand: boolean) => {
  if (!el) return
  if (!expand) {
    if (el.style.maxHeight === '' || el.style.maxHeight === 'none') {
      el.style.maxHeight = `${el.clientHeight}px`
      void el.clientHeight // reflow
    }
    el.style.maxHeight = '0px'
    return
  }
  el.style.maxHeight = `${el.scrollHeight}px`
}

const onTransitionEndToAuto = (
  el: HTMLElement | null,
  activeRef: () => boolean
) => {
  if (!el) return () => {}
  const handler = (evt: TransitionEvent) => {
    if (evt.propertyName === 'max-height' && activeRef()) {
      el.style.maxHeight = 'none'
    }
  }
  el.addEventListener('transitionend', handler)
  return () => el.removeEventListener('transitionend', handler)
}

// =======================
// Question Accordion
// =======================
const QuestionAccordion: React.FC<
  QuestionProps & {
    currentHash: string | false
    onToggle?: FaqsProps['onAccordionToggle']
  }
> = ({ question, answer, sectionID, currentHash, onToggle }) => {
  const qKey = `FAQ-${sectionID}-${toKey(question)}`
  const [active, setActive] = useState(Boolean(currentHash))
  const contentRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const desired = Boolean(currentHash)
    if (desired !== active) setActive(desired)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHash])

  useEffect(() => {
    initCollapsed(contentRef.current, active)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    animateHeight(contentRef.current, active)
    const cleanup = onTransitionEndToAuto(contentRef.current, () => active)
    return cleanup
  }, [active])

  const onClick = () => {
    const next = !active
    setActive(next)
    onToggle?.({ key: qKey, state: next ? 'open' : 'closed', level: 'question' })
    if (currentHash && isBrowser) {
      const { pathname, search } = window.location
      window.history.pushState('', document.title, pathname + search)
    }
  }

  return (
    <FaqQuestionWrapper id={qKey}>
      <FaqQuestionTitle active={active} onClick={onClick}>
        {question}
      </FaqQuestionTitle>
      <FaqQuestionAnswer ref={contentRef} active={active}>
        {answer} {/* <-- render as children */}
      </FaqQuestionAnswer>
    </FaqQuestionWrapper>
  )
}

// =======================
// Section Accordion
// =======================
const SectionAccordion: React.FC<
  SectionProps & {
    currentHash: string | false
    onToggle?: FaqsProps['onAccordionToggle']
  }
> = ({ sectionID, sectionTitle, questions, currentHash, onToggle }) => {
  const sKey = `FAQ-${sectionID}`
  const [active, setActive] = useState(Boolean(currentHash))
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const desired = Boolean(currentHash)
    if (desired !== active) setActive(desired)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHash])

  useEffect(() => {
    initCollapsed(contentRef.current, active)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    animateHeight(contentRef.current, active)
    const cleanup = onTransitionEndToAuto(contentRef.current, () => active)
    return cleanup
  }, [active])

  const onClick = () => {
    const next = !active
    setActive(next)
    onToggle?.({ key: sKey, state: next ? 'open' : 'closed', level: 'section' })
    if (currentHash && isBrowser) {
      const { pathname, search } = window.location
      window.history.pushState('', document.title, pathname + search)
    }
  }

  return (
    <FaqSectionWrapper id={sKey}>
      <FaqSectionTitle active={active} onClick={onClick}>
        {sectionTitle}
      </FaqSectionTitle>

      <FaqQuestionsWrapper ref={contentRef} active={active}>
        {questions.map((q) => {
          const qHashHit =
            currentHash && currentHash.includes(toKey(q.question)) ? currentHash : false
          return (
            <QuestionAccordion
              key={toKey(q.question)}
              sectionID={sectionID}
              currentHash={qHashHit}
              onToggle={onToggle}
              {...q}
            />
          )
        })}
      </FaqQuestionsWrapper>
    </FaqSectionWrapper>
  )
}

// =======================
// Public Component
// =======================
export const Faqs: React.FC<FaqsProps> = ({
  sectionHeaderProps = { color: '#000', borderColor: '#cfcecc' },
  faqSections = DefaultFaqsSections,
  onAccordionToggle,
}) => {
  const [hash, setHash] = useState<string>(() =>
    isBrowser ? window.location.hash.substring(1) : ''
  )

  useEffect(() => {
    if (!isBrowser) return
    const handler = () => setHash(window.location.hash.substring(1))
    handler()
    window.addEventListener('hashchange', handler)
    return () => window.removeEventListener('hashchange', handler)
  }, [])

  return (
    <div>
      <SectionHeader
        label="Questions?"
        color={sectionHeaderProps.color}
        borderColor={sectionHeaderProps.borderColor}
      />
      <FaqSectionsWrapper>
        {faqSections.map((section) => {
          const sectionHit = hash && hash.includes(section.sectionID) ? hash : ''
          return (
            <SectionAccordion
              key={section.sectionID}
              currentHash={sectionHit || false}
              onToggle={onAccordionToggle}
              {...section}
            />
          )
        })}
      </FaqSectionsWrapper>
    </div>
  )
}

// =======================
// Default Content (static host)
// =======================
const currentHost = 'www.pizzahut.com'

export const DefaultFaqsSections: Array<SectionProps> = [
  {
    sectionID: 'enrollment',
    sectionTitle: 'Enrollment',
    questions: [
      {
        question: 'Who can join?',
        answer: (
          <>
            Hut Rewards is open to legal residents of the fifty United States
            and District of Columbia who are, starting on Jul 16, 2024 at least
            eighteen (18) years old. To see if your local Pizza Hut is
            participating in Hut Rewards,&nbsp;
            <a href={`https://${currentHost}/index.php?locator=#/find-a-hut`} target="_top">
              <strong><u>click here</u></strong>
            </a>.
          </>
        ),
      },
      {
        question: 'How do I enroll?',
        answer: (
          <>
            To join, visit{' '}
            <a href={`https://${currentHost}/account/join`} target="_top">
              <strong><u>Hut Rewards</u></strong>
            </a>{' '}
            to learn about the loyalty program and click ‘Sign Up’ to enroll.
          </>
        ),
      },
      {
        question: 'What stores are participating in Hut Rewards?',
        answer: (
          <>
            To see if your local Pizza Hut is participating,&nbsp;
            <a href={`https://${currentHost}/index.php?locator=#/find-a-hut`} target="_top">
              <strong><u>click here</u></strong>
            </a>.
          </>
        ),
      },
      {
        question:
          'My Store Closed! What happens to my Hut Rewards points, BOOK IT! rewards, and gift cards?',
        answer: (
          <>
            Gift cards, Hut Rewards points, and BOOK IT! rewards remain valid.
            To find another Pizza Hut near you,&nbsp;
            <a href={`https://${currentHost}/index.php?locator=#/find-a-hut`} target="_top">
              <strong><u>click here</u></strong>
            </a>.
          </>
        ),
      },
    ],
  },
  {
    sectionID: 'earning',
    sectionTitle: 'Earning Points',
    questions: [
      {
        question: 'How do I earn points?',
        answer: (
          <>
            After enrollment, earn 2 points for every whole $1 spent at
            participating stores, including delivery fees (excludes taxes, tips,
            donations, third-party deliveries, school/cafeteria purchases, and
            gift cards). Points usually post within 24–48 hours and are rounded
            down.
          </>
        ),
      },
      { question: 'Do my points expire?', answer: <>Points expire after 6 months of inactivity; keep earning or redeeming to keep them active.</> },
      { question: 'Can I earn points on taxes and delivery fees?', answer: <>Delivery fees count toward points; taxes, tips, donations, certain programs, and gift cards do not.</> },
      {
        question: 'Can I earn points on phone orders or in-store?',
        answer: (
          <>
            Yes—dine-in, carryout, and delivery. Provide the phone number on
            your Hut Rewards account. For participating stores,&nbsp;
            <a href={`https://${currentHost}/index.php?locator=#/find-a-hut`} target="_top">
              <strong><u>click here</u></strong>
            </a>.
          </>
        ),
      },
      { question: 'What are pending points?', answer: <>Pending points appear after purchase and are unavailable until verified (typically 24–48 hours).</> },
      { question: 'What are promotional points and promotional offers?', answer: <>Promotional points appear as a separate line item and are subject to specific offer terms.</> },
      { question: 'Where do I go to see my points balance?', answer: <>Once logged in on PizzaHut.com, see the balance next to the Hut Rewards tab or in Manage Account.</> },
      { question: 'Can I earn points on gift card purchases?', answer: <>Gift card purchases are not eligible for points, but you can pay with gift cards and earn on that order.</> },
      { question: `I placed a future order, why can't I use my points?`, answer: <>Points deposit after you receive your order (typically 24–48 hours). Until then, they show as pending.</> },
      { question: 'Can I get credit for past orders?', answer: <>Points only accrue after successful enrollment and cannot be applied retroactively to guest transactions.</> },
    ],
  },
  {
    sectionID: 'redeeming',
    sectionTitle: 'Redeeming Points & Offers',
    questions: [
      { question: 'How do I redeem points?', answer: <>Log in on the website or app. If eligible, click “redeem” to add a free reward or build a free pizza. Points deduct when the order is placed. Online redemption only.</> },
      { question: 'How many points do I need to redeem for a reward?', answer: <>Items and point values vary and may change without notice. See current options online.</> },
      { question: 'What can I get with a Medium 2-topping pizza?', answer: <>Medium 2-topping on Hand-Tossed or Thin ’N Crispy®. Upcharges apply for extra cheese, Stuffed Crust®, Original Pan®, or additional toppings.</> },
      { question: 'What can I get with an ANY™ pizza?', answer: <>Large ANY pizza (any toppings/recipes) on Original Pan®, Hand-Tossed, or Thin ’N Crispy®. Upcharges for Stuffed Crust® or extra cheese.</> },
      { question: 'Can I upgrade my reward pizza to Original Stuffed Crust®?', answer: <>Yes, where available, for an additional charge.</> },
      { question: 'What are exclusive offers?', answer: <>Occasional member-exclusive promotions subject to specific terms.</> },
      { question: `What if my order times out or I don't complete my order?`, answer: <>Your redeemed item will still be in your cart. Reapply from your dashboard if needed.</> },
      { question: 'Can I change to a different reward?', answer: <>Yes. In the cart, select UNDO or REMOVE on the free item to free your points and choose a different reward.</> },
      { question: `Can I change my reward if I didn't complete my order or my order timed out?`, answer: <>Yes. Reapply to the cart and select UNDO to free your points and pick another reward.</> },
      { question: 'Can I pay with a gift card?', answer: <>Yes. You can pay with a gift card on Hut Rewards transactions (earning on gift card purchases is not available).</> },
      { question: 'What happens to a re-order that included a reward pizza?', answer: <>Re-orders will show the item at menu price. Remove it if you do not want it.</> },
      { question: 'Is there a delivery minimum on orders with a free reward product?', answer: <>Yes. Delivery charges and minimums vary by store. The delivery charge is not a driver tip.</> },
      { question: 'How many reward products can I redeem?', answer: <>As many as you have points for. Each reward must be redeemed via your account.</> },
      { question: 'Do I have to pay for anything in conjunction with my free reward product?', answer: <>You are responsible for taxes, delivery fees, and any delivery minimums. The delivery charge is not a driver tip.</> },
      { question: 'Can I redeem my points in stores?', answer: <>Not at this time. Redemptions are online only for carryout or delivery.</> },
    ],
  },
  {
    sectionID: 'account',
    sectionTitle: 'Account Management',
    questions: [
      { question: 'Where do I go to see my points balance?', answer: <>Once logged in, your balance appears next to the Hut Rewards tab; it is also in your dashboard under Manage Account.</> },
      { question: 'Can I combine Hut Rewards accounts?', answer: <>No. Accounts cannot be combined.</> },
      {
        question: 'How do I Close My Hut Rewards Account?',
        answer: (
          <>
            Log in, open the Hut Rewards tab, and click “Close My Account.”
            Confirm via the email sent to you. Closing forfeits points and deletes
            the Hut Rewards account (not your general PizzaHut.com account). For
            help, use{' '}
            <a href={`https://${currentHost}/index.php#/contactus`} target="_parent">
              <strong><u>Contact Us</u></strong>
            </a>.
          </>
        ),
      },
      { question: 'What happens to my points if I choose to leave the program?', answer: <>You forfeit all accrued points and the Hut Rewards account is deleted (your general web account remains).</> },
      { question: 'How do I opt-out of emails?', answer: <>Click the unsubscribe link in an email or manage preferences under My Account → Communication Preferences.</> },
      { question: 'Where are the full terms and conditions of the program?', answer: <>See the Hut Rewards Terms and Conditions and the site Terms of Use (including Dispute Resolution/Arbitration).</> },
      {
        question: 'Where do I go if I need help?',
        answer: (
          <>
            Use{' '}
            <a href={`https://${currentHost}/index.php#/contactus`} target="_parent">
              <strong><u>Contact Us</u></strong>
            </a>{' '}
            for Hut Rewards support.
          </>
        ),
      },
    ],
  },
]
